import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import SvgIcon from '@mui/material/SvgIcon';

import { withLang } from '_core/hocs/withLang';
import { TextInputBase } from '_core/components/TextInputBase';

import { useStyle } from './style';

const ClipboardIcon = ({ className }) => (
  <SvgIcon className={className}>
    <path d="M17,9H7V7H17M17,13H7V11H17M14,17H7V15H14M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3M19,3H14.82C14.4,1.84 13.3,1 12,1C10.7,1 9.6,1.84 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3Z" />
  </SvgIcon>
);

const ClipboardInnerInput = ({ inputRef, onCopy, ...props }) => (
  <CopyToClipboard text={props.value} onCopy={onCopy}>
    <input ref={inputRef} {...props} />
  </CopyToClipboard>
);

const DEFAULT_END_ADORNMENT_COMPONENT = ({
  value,
  adornmentClassName,
  iconClassName,
  onCopy,
  children
}) => (
  <InputAdornment position="end" className={adornmentClassName}>
    <CopyToClipboard text={value} onCopy={onCopy}>
      <IconButton size="small">
        <ClipboardIcon className={iconClassName} />
      </IconButton>
    </CopyToClipboard>
  </InputAdornment>
);

const ELEMENT_END_ADORNMENT_COMPONENT = ({
  value,
  adornmentClassName,
  iconClassName,
  adornment,
  onCopy,
  children
}) => (
  <InputAdornment position="end" className={adornmentClassName}>
    {adornment}

    <CopyToClipboard text={value} onCopy={onCopy}>
      <IconButton size="small">
        <ClipboardIcon className={iconClassName} />
      </IconButton>
    </CopyToClipboard>
  </InputAdornment>
);

const RawClipboardInput = (props) => {
  const {
    className,
    value,
    label,
    disabled,
    readOnly,
    render,
    Component,
    getLangString: l,
    endAdornment: endAdornmentProp,

    ...rest
  } = props;

  delete rest.langInfo;
  delete rest.getLangObject;
  delete rest.getLangStringSet;
  delete rest.getFirstLangString;

  const classes = useStyle();

  const copyTimeoutRef = React.useRef();
  const [isCopied, setIsCopied] = React.useState(false);

  const aValue = !value ? l('r.zagruzka') : value;
  const aLabel = (isCopied ? l('r.skopirovano') : label) || ' ';

  const rootClasses = clsx({
    [classes.root]: true,
    [className]: className != null
  });

  const EndAdornmentComponent =
    endAdornmentProp === undefined
      ? DEFAULT_END_ADORNMENT_COMPONENT
      : React.isValidElement(endAdornmentProp)
      ? ELEMENT_END_ADORNMENT_COMPONENT
      : endAdornmentProp;

  const onCopy = () => {
    if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);

    setIsCopied(true);

    copyTimeoutRef.current = window.setTimeout(() => setIsCopied(false), 2000);
  };

  const endAdornment = (
    <EndAdornmentComponent
      value={aValue}
      adornment={endAdornmentProp}
      adornmentClassName={classes.adornment}
      iconClassName={isCopied ? classes.iconCopied : classes.icon}
      onCopy={onCopy}
    />
  );

  const childProps = {
    disabled,
    readOnly,
    value: aValue,
    label: aLabel,
    success: isCopied,
    endAdornment,

    InputProps: {
      inputComponent: ClipboardInnerInput,
      endAdornment,

      inputProps: {
        onCopy
      }
    },

    ...rest
  };

  return (
    <div className={rootClasses}>
      {render ? (
        render(childProps)
      ) : Component ? (
        <Component {...childProps} />
      ) : (
        <TextInputBase
          classes={{
            input: classes.input,
            label: classes.inputLabel,
            helperText: classes.inputHelperText
          }}
          {...childProps}
          {...rest}
        />
      )}
    </div>
  );
};

RawClipboardInput.defaultProps = {
  Component: TextInputBase,
  readOnly: true,
  endAdornment: DEFAULT_END_ADORNMENT_COMPONENT
};

RawClipboardInput.propTypes = {
  // self props
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  render: PropTypes.func,
  Component: PropTypes.elementType,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired
};

export const ClipboardInput = withLang(RawClipboardInput);
